import React, { useEffect, useState } from "react";
import { MapContainer, Polyline, Polygon, Marker, useMap } from "react-leaflet";
import { MapControlStack, TraffixTileLayer } from "../../../../common/map";
import "leaflet/dist/leaflet.css";
import LosGraph from './Los_Graph';
import L from 'leaflet';
// Styles are loaded via index.css in the parent component
import trafficLightIcon from './TrafficLightIcon';
import { semaforoIcon, rotatoriaIcon } from './NodeTypeIcons';
import { useDataContext } from '../../context/DataContext';
import {
  SCORE_SCALE_WARN, SCORE_SCALE_WORST, selectionAwareColor,
} from '../../../../constants/scoreScale';

const NODE_COLOR = '#8FC975';

// Custom pulsing icon for selected nodes - uses HTML div with CSS animation
const createPulsingIcon = () => {
  return L.divIcon({
    className: 'pulsing-marker-container',
    html: `
      <div class="pulsing-marker">
        <div class="pulsing-marker-ring"></div>
        <div class="pulsing-marker-ring pulsing-marker-ring-delayed"></div>
        <div class="pulsing-marker-ring pulsing-marker-ring-third"></div>
      </div>
    `,
    iconSize: [80, 80],
    iconAnchor: [40, 40],
  });
};

function ZoomControl({ onZoomChange }) {
  return (
    <MapControlStack
      showLocation={false}
      className="simfix-map-controls"
      zoomClassName="simfix-zoom"
      zoomButtonClassName="simfix-zoom-btn"
      onZoom={onZoomChange}
    />
  );
}

// Component to sync zoom from external source (for comparison mode)
// Uses debouncing and local tracking to prevent feedback loops
function ZoomSynchronizer({ targetZoom, mapIndex, zoomSourceMap }) {
  const map = useMap();
  const isApplyingZoom = React.useRef(false);
  const lastTargetZoom = React.useRef(targetZoom);

  useEffect(() => {
    // Only sync if:
    // 1. We have a target zoom
    // 2. This map is NOT the source (different mapIndex)
    // 3. We're not currently applying a zoom
    // 4. The target actually changed
    if (
      targetZoom !== null &&
      zoomSourceMap !== null &&
      zoomSourceMap !== mapIndex &&
      !isApplyingZoom.current &&
      lastTargetZoom.current !== targetZoom
    ) {
      const currentZoom = map.getZoom();
      if (Math.abs(currentZoom - targetZoom) > 0.1) {
        isApplyingZoom.current = true;
        lastTargetZoom.current = targetZoom;
        map.setZoom(targetZoom, { animate: false });
        // Reset the flag after a short delay
        setTimeout(() => {
          isApplyingZoom.current = false;
        }, 100);
      }
    }
  }, [targetZoom, mapIndex, zoomSourceMap, map]);

  return null;
}

// Component to track zoom level
function ZoomHandler({ onZoomChange }) {
  const map = useMap();

  useEffect(() => {
    const handleZoom = () => {
      onZoomChange(map.getZoom());
    };

    // Set initial zoom
    onZoomChange(map.getZoom());

    map.on('zoomend', handleZoom);

    return () => {
      map.off('zoomend', handleZoom);
    };
  }, [map, onZoomChange]);

  return null;
}




const LegendControl = () => {
  const [legendAdded, setLegendAdded] = useState(false);
  const map = useMap();

  useEffect(() => {
    if (!legendAdded) {
      const legend = L.control({ position: 'bottomright' });

      legend.onAdd = function (map) {
        const div = L.DomUtil.create('div', 'map-legend-control');
        div.innerHTML = `
          <div class="map-legend-content">
            <div class="map-legend-item">
              <p class="map-legend-label">Coda Max:</p>
              <div class="map-legend-line map-legend-line-max"></div>
            </div>
            <div class="map-legend-item">
              <p class="map-legend-label">Coda Media:</p>
              <div class="map-legend-line map-legend-line-media"></div>
            </div>
          </div>
        `;

        return div;
      };

      legend.addTo(map);
      setLegendAdded(true);
    }
  }, [legendAdded, map]);

  return null;
};

const SimpleMap = ({
  selectedIdScenario,
  selectedId,
  onSelectNode,
  selectedRoadId,
  onSelectRoad,
  fullscreen = false,
  zoomLevel = 17,
  onZoomChange,
  mapCenter,
  onCenterChange,
  showZoomControl = true,
  syncZoom = null, // External zoom level for synchronization in comparison mode
  onSyncZoomChange = null, // Callback when zoom changes (for sync)
  mapIndex = 0, // Index to identify which map (0 or 1) for sync
  zoomSourceMap = null // Which map initiated the zoom (0 or 1)
}) => {
  // Get data from context
  const {
    dataJson: contextDataJson,
    losDataJson,
    dataLoaded
  } = useDataContext();

  const [scenarioData, setScenarioData] = useState({});
  const [currentZoom, setCurrentZoom] = useState(zoomLevel);

  // Get scenario data from context when selectedIdScenario changes
  useEffect(() => {
    if (!dataLoaded || !selectedIdScenario) {
      setScenarioData({});
      return;
    }

    // Convert selectedIdScenario to number for comparison if it's a string
    const scenarioId = typeof selectedIdScenario === 'string' ?
      (isNaN(selectedIdScenario) ? selectedIdScenario : Number(selectedIdScenario)) :
      selectedIdScenario;

    const selectedScenario = contextDataJson?.scenario?.find(scenario =>
      scenario.replicaAvg === scenarioId
    );

    if (selectedScenario) {
      // Create a copy to avoid mutating context data
      const scenarioCopy = JSON.parse(JSON.stringify(selectedScenario));

      // Merge tipo from losDataJson into nodiAimsun
      if (losDataJson?.scenario && scenarioCopy.nodiAimsun) {
        const losScenario = losDataJson.scenario.find(s => s.replicaAvg === scenarioId);
        if (losScenario?.LosNodi) {
          scenarioCopy.nodiAimsun = scenarioCopy.nodiAimsun.map(node => {
            const losNode = losScenario.LosNodi.find(ln => ln.idNodo === node.id || ln.idNodo === parseFloat(node.id));
            return {
              ...node,
              tipo: losNode?.tipo || node.tipo || '',
              nome: losNode?.nome || node.name || ''
            };
          });
        }
      }
      setScenarioData(scenarioCopy);
      console.log('Map updated with scenario:', scenarioCopy.scName, scenarioCopy.center);
    } else {
      console.log('No scenario found for ID:', selectedIdScenario);
      setScenarioData({});
    }
  }, [selectedIdScenario, contextDataJson, losDataJson, dataLoaded]);

  // Use scenarioData instead of dataJson
  const dataJson = scenarioData;

  const handleNodeSelection = (index) => {
    const node = dataJson.nodiAimsun[index];
    if (node) {
      onSelectNode(node.id); // Update the selectedId in the parent component
    } else {
      onSelectNode(null); // Clear selection if no node is found
    }
  };

  // Helper function to calculate the center of a polygon
  const getPolygonCenter = (coordinates) => {
    if (!coordinates || coordinates.length === 0) return null;
    
    let latSum = 0;
    let lngSum = 0;
    
    coordinates.forEach(coord => {
      latSum += coord[0];
      lngSum += coord[1];
    });
    
    return [latSum / coordinates.length, lngSum / coordinates.length];
  };

  const optionsOSM = { color: '#A3B2C9' };
  const optionCodaMax = { color: SCORE_SCALE_WORST };
  const optionsCodaMedia = { color: SCORE_SCALE_WARN };
  const hasMapSelection = Boolean(selectedId || selectedRoadId);
  const optionsFer = { color: '#470d16b3', dashArray: '2, 10' };

  // Show a loading state or default map if no data yet
  if (!dataJson || !dataJson.center) {
    // Return a default map centered on a default location
    const defaultCenter = [45.640301546939185, 9.611915782993783]; // Default coordinates
    const defaultZoom = 17;

    if (fullscreen) {
      return (
        <MapContainer center={mapCenter || defaultCenter} zoom={zoomLevel || defaultZoom} style={{ height: "100%", width: "100%" }} zoomControl={false} preferCanvas={true} zoomSnap={0} zoomDelta={0.5} wheelDebounceTime={0} wheelPxPerZoomLevel={120} zoomAnimation={false}>
          <TraffixTileLayer theme="dark" />
          {showZoomControl && <ZoomControl onZoomChange={(zoom) => onSyncZoomChange && onSyncZoomChange(zoom, mapIndex)} />}
          {syncZoom !== null && <ZoomSynchronizer targetZoom={syncZoom} mapIndex={mapIndex} zoomSourceMap={zoomSourceMap} />}

        </MapContainer>
      );
    }
    return (
      <div className="container-all-confronto">
        <div className="map-container-confronto">
          <MapContainer center={mapCenter || defaultCenter} zoom={zoomLevel || defaultZoom} style={{ height: "100%", width: "100%" }} preferCanvas={true} zoomSnap={0} zoomDelta={0.5} wheelDebounceTime={0} wheelPxPerZoomLevel={120} zoomAnimation={false}>
            <TraffixTileLayer theme="light" />
          </MapContainer>
        </div>
        <div className="los-container-confronto"></div>
      </div>
    );
  }

  // Set the center and zoom based on props or dataJson
  const center = mapCenter || [dataJson.center[1], dataJson.center[0]];
  const zoom = zoomLevel || 17;

  // For fullscreen mode, return just the map without the LOS container
  if (fullscreen) {
    return (
      <MapContainer key={`map-${center[0]}-${center[1]}`} center={center} zoom={zoom} style={{ height: "100%", width: "100%" }} zoomControl={false} preferCanvas={true} zoomSnap={0} zoomDelta={0.5} wheelDebounceTime={0} wheelPxPerZoomLevel={120} zoomAnimation={false}>
        <TraffixTileLayer theme="dark" />
        {showZoomControl && <ZoomControl onZoomChange={(zoom) => onSyncZoomChange && onSyncZoomChange(zoom, mapIndex)} />}
        {/* Synchronized zoom for comparison mode */}
        {syncZoom !== null && <ZoomSynchronizer targetZoom={syncZoom} mapIndex={mapIndex} zoomSourceMap={zoomSourceMap} />}

        {dataJson.nodiAimsun && dataJson.nodiAimsun.map((item, index) => {
          // Use original polygon coordinates
          const coords = item.geometryNodi.coordinates;
          const isSelected = selectedId === item.id;

          return (
            <Polygon
              positions={coords}
              eventHandlers={{
                click: () => handleNodeSelection(index),
              }}
              key={item.id}
              pathOptions={{
                color: selectionAwareColor(isSelected, hasMapSelection, NODE_COLOR),
                fillColor: selectionAwareColor(isSelected, hasMapSelection, NODE_COLOR),
                fillOpacity: hasMapSelection && !isSelected ? 0.4 : 0.7,
                weight: 2,
              }}
            />
          );
        })}

        {/* Pulsing indicator for selected node - HTML div with CSS animation */}
        {selectedId && dataJson.nodiAimsun && dataJson.nodiAimsun.map((item) => {
          if (item.id !== selectedId) return null;
          const center = getPolygonCenter(item.geometryNodi.coordinates);
          if (!center) return null;

          return (
            <Marker
              key={`pulse-${item.id}`}
              position={center}
              icon={createPulsingIcon()}
              zIndexOffset={1000}
            />
          );
        })}

        {/* Node Type Icons - Show at zoom level 18+ */}
        <ZoomHandler onZoomChange={(zoom) => {
          setCurrentZoom(zoom);
          if (onSyncZoomChange) onSyncZoomChange(zoom, mapIndex);
        }} />
        {currentZoom >= 18 && dataJson.nodiAimsun && dataJson.nodiAimsun.map((item, index) => {
          const center = getPolygonCenter(item.geometryNodi.coordinates);
          if (!center) return null;

          // Debug logging
          if (index === 0) {
            console.log('Sample node data:', {
              id: item.id,
              name: item.name,
              nome: item.nome,
              tipo: item.tipo,
              hasName: !!item.name,
              hasNome: !!item.nome,
              nameStartsWithRotatoria: item.name ? item.name.toLowerCase().startsWith('rotatoria') : false,
              nomeStartsWithRotatoria: item.nome ? item.nome.toLowerCase().startsWith('rotatoria') : false
            });
          }

          let icon = null;
          // Check tipo field for semaforo
          if (item.tipo === 'semaforo') {
            icon = semaforoIcon;
          }
          // Check tipo field for rotatoria
          else if (item.tipo === 'rotatoria' || item.tipo === 'Rotatoria') {
            icon = rotatoriaIcon;
          }
          // Check if name starts with "Rotatoria" (case-insensitive)
          else if ((item.name && item.name.toLowerCase().startsWith('rotatoria')) ||
                   (item.nome && item.nome.toLowerCase().startsWith('rotatoria'))) {
            icon = rotatoriaIcon;
          }

          if (!icon) return null;

          return (
            <Marker
              key={`icon-${item.id}`}
              position={center}
              icon={icon}
              zIndexOffset={500}
            />
          );
        })}

        {/* Display traffic lights for all intersections that have them */}
        {dataJson.nodiAimsun && dataJson.nodiAimsun.filter(node =>
          node.positionMarker && node.positionMarker.length === 2
        ).map(node => {
          const markerPos = node.positionMarker;
          console.log('Traffic light at node:', node.id, 'Position:', markerPos);
          return (
            <Marker
              key={`traffic-${node.id}`}
              position={[markerPos[0], markerPos[1]]}
              icon={trafficLightIcon}
              zIndexOffset={1000} // Ensure traffic lights appear above other elements
            />
          );
        })}

        {dataJson.geometryOSM && dataJson.geometryOSM.length > 0 && dataJson.geometryOSM.filter(item => {
          // Handle both object format {id, geometry} and direct coordinate array format
          const coords = item?.geometry || item;
          return Array.isArray(coords) && coords.length > 0;
        }).map((item, index) => {
          const coords = item?.geometry || item;
          const osmId = item?.id || index;
          const roadId = `osm-${osmId}`;
          const isSelectedRoad = selectedRoadId === roadId;

          return (
            <Polyline
              key={roadId}
              pathOptions={{
                ...optionsOSM,
                weight: isSelectedRoad ? 6 : 3,
                opacity: isSelectedRoad ? 1 : (hasMapSelection ? 0.45 : 0.6),
                color: selectionAwareColor(isSelectedRoad, hasMapSelection, optionsOSM.color)
              }}
              positions={coords}
              eventHandlers={{
                click: () => {
                  if (onSelectRoad) {
                    // Find which node this street belongs to
                    // Pass the OSM ID and let the parent handle finding the node
                    onSelectRoad(roadId, {
                      osmId: osmId,
                      osmName: (item?.display_name && item.display_name !== null) ? item.display_name : (item?.name || null),
                      coordinates: coords
                    });
                  }
                }
              }}
            />
          );
        })}

        {/* Glowing polyline effect for selected OSM road - follows street shape */}
        {selectedRoadId && dataJson.geometryOSM && dataJson.geometryOSM.map((item, index) => {
          const coords = item?.geometry || item;
          const osmId = item?.id || index;
          const roadId = `osm-${osmId}`;
          if (roadId !== selectedRoadId) return null;

          if (!Array.isArray(coords) || coords.length === 0) return null;

          return (
            <React.Fragment key={`glow-road-${osmId}`}>
              {/* Outer glow layer 1 - largest, most transparent */}
              <Polyline
                positions={coords}
                pathOptions={{
                  color: '#8C74FF',
                  weight: 20,
                  opacity: 0.3,
                  className: 'road-glow-outer'
                }}
                className="road-glow-outer"
              />
              {/* Middle glow layer 2 */}
              <Polyline
                positions={coords}
                pathOptions={{
                  color: '#8C74FF',
                  weight: 14,
                  opacity: 0.5,
                  className: 'road-glow-middle'
                }}
                className="road-glow-middle"
              />
              {/* Inner glow layer 3 - brightest */}
              <Polyline
                positions={coords}
                pathOptions={{
                  color: '#8C74FF',
                  weight: 8,
                  opacity: 0.8,
                  className: 'road-glow-inner'
                }}
                className="road-glow-inner"
              />
            </React.Fragment>
          );
        })}

        {/* Glowing polyline effect for selected coda media road - follows street shape */}
        {selectedRoadId && selectedId && dataJson.nodiAimsun && dataJson.nodiAimsun.map(node => {
          if (node.id !== selectedId) return null;
          
          return node.codePerNodo[1]?.codeMedie?.geometryCodaMedia?.map((coordinates, index) => {
            const roadId = `${node.id}-road-${index}`;
            if (roadId !== selectedRoadId) return null;
            
            if (!coordinates || coordinates.length === 0) return null;

            return (
              <React.Fragment key={`glow-road-${node.id}-${index}`}>
                {/* Outer glow layer 1 - largest, most transparent */}
                <Polyline
                  positions={coordinates}
                  pathOptions={{
                    color: '#8C74FF',
                    weight: 20,
                    opacity: 0.3,
                    className: 'road-glow-outer'
                  }}
                  className="road-glow-outer"
                />
                {/* Middle glow layer 2 */}
                <Polyline
                  positions={coordinates}
                  pathOptions={{
                    color: '#8C74FF',
                    weight: 14,
                    opacity: 0.5,
                    className: 'road-glow-middle'
                  }}
                  className="road-glow-middle"
                />
                {/* Inner glow layer 3 - brightest */}
                <Polyline
                  positions={coordinates}
                  pathOptions={{
                    color: '#8C74FF',
                    weight: 8,
                    opacity: 0.8,
                    className: 'road-glow-inner'
                  }}
                  className="road-glow-inner"
                />
              </React.Fragment>
            );
          });
        })}

        {dataJson.nodiAimsun && dataJson.nodiAimsun.map(node => {
          if (node.id === selectedId) {
            const geometryCodaMax = node.codePerNodo[0]?.codeMax?.geometryCodaMax;
            if (geometryCodaMax) {
              const parsedCoordinates = geometryCodaMax.map(coordinates => coordinates.map(coordinate => [coordinate[0], coordinate[1]]));
              return <Polyline key={`coda-max-${node.id}`} pathOptions={optionCodaMax} positions={parsedCoordinates} />;
            }
          }
          return null;
        })}

        {/* Display coda media roads - only show when a node is selected */}
        {selectedId && dataJson.nodiAimsun && dataJson.nodiAimsun.map(node => {
          // Only show coda media for the selected node
          if (node.id !== selectedId) {
            return null;
          }

          return node.codePerNodo[1]?.codeMedie && node.codePerNodo[1]?.codeMedie.geometryCodaMedia &&
            node.codePerNodo[1]?.codeMedie.geometryCodaMedia.map((coordinates, index) => {
              const roadId = `${node.id}-road-${index}`;
              const isSelectedRoad = selectedRoadId === roadId;

              return (
                <Polyline
                  key={`coda-media-${node.id}-${index}`}
                  pathOptions={{
                    ...optionsCodaMedia,
                    weight: isSelectedRoad ? 6 : 3,
                    opacity: isSelectedRoad ? 1 : 0.8,
                    color: selectionAwareColor(isSelectedRoad, Boolean(selectedRoadId), optionsCodaMedia.color)
                  }}
                  positions={coordinates}
                  eventHandlers={{
                    click: () => {
                      if (onSelectRoad) {
                        onSelectRoad(roadId, {
                          nodeId: node.id,
                          roadIndex: index,
                          coordinates: coordinates
                        });
                      }
                    }
                  }}
                />
              );
            });
        })}
      </MapContainer>
    );
  }

  // Original layout for non-fullscreen mode (kept for backward compatibility)
  return (
    <div className="container-all-confronto">
      <div className="map-container-confronto" >
        <MapContainer key={`map-${center[0]}-${center[1]}`} center={center} zoom={zoom} style={{ height: "100%", width: "100%" }} preferCanvas={true} zoomSnap={0} zoomDelta={0.5} wheelDebounceTime={0} wheelPxPerZoomLevel={120} zoomAnimation={false}>
          <TraffixTileLayer theme="dark" />
          <LegendControl />
          {dataJson.nodiAimsun && dataJson.nodiAimsun.map((item, index) => {
            // Use original polygon coordinates
            const coords = item.geometryNodi.coordinates;
            const isSelected = selectedId === item.id;

            return (
              <Polygon
                positions={coords}
                eventHandlers={{
                  click: () => handleNodeSelection(index),
                }}
                key={item.id}
                pathOptions={{
                  color: selectionAwareColor(isSelected, hasMapSelection, NODE_COLOR),
                  fillColor: selectionAwareColor(isSelected, hasMapSelection, NODE_COLOR),
                  fillOpacity: hasMapSelection && !isSelected ? 0.4 : 0.7,
                  weight: 2,
                }}
              />
            );
          })}

          {/* Pulsing indicator for selected node - HTML div with CSS animation */}
          {selectedId && dataJson.nodiAimsun && dataJson.nodiAimsun.map((item) => {
            if (item.id !== selectedId) return null;
            const center = getPolygonCenter(item.geometryNodi.coordinates);
            if (!center) return null;

            return (
              <Marker
                key={`pulse-${item.id}`}
                position={center}
                icon={createPulsingIcon()}
                zIndexOffset={1000}
              />
            );
          })}

          {/* Node Type Icons - Show at zoom level 18+ */}
          <ZoomHandler onZoomChange={setCurrentZoom} />
          {currentZoom >= 18 && dataJson.nodiAimsun && dataJson.nodiAimsun.map((item, index) => {
            const center = getPolygonCenter(item.geometryNodi.coordinates);
            if (!center) return null;

            let icon = null;
            // Check tipo field for semaforo
            if (item.tipo === 'semaforo') {
              icon = semaforoIcon;
            }
            // Check tipo field for rotatoria
            else if (item.tipo === 'rotatoria' || item.tipo === 'Rotatoria') {
              icon = rotatoriaIcon;
            }
            // Check if name starts with "Rotatoria" (case-insensitive)
            else if ((item.name && item.name.toLowerCase().startsWith('rotatoria')) ||
                     (item.nome && item.nome.toLowerCase().startsWith('rotatoria'))) {
              icon = rotatoriaIcon;
            }

            if (!icon) return null;

            return (
              <Marker
                key={`icon-${item.id}`}
                position={center}
                icon={icon}
                zIndexOffset={500}
              />
            );
          })}

          {/* Display traffic lights for all intersections that have them */}
          {dataJson.nodiAimsun && dataJson.nodiAimsun.filter(node =>
            node.positionMarker && node.positionMarker.length === 2
          ).map(node => {
            const markerPos = node.positionMarker;
            console.log('Traffic light at node:', node.id, 'Position:', markerPos);
            return (
              <Marker
                key={`traffic-${node.id}`}
                position={[markerPos[0], markerPos[1]]}
                icon={trafficLightIcon}
                zIndexOffset={1000} // Ensure traffic lights appear above other elements
              />
            );
          })}

          {dataJson.geometryOSM && dataJson.geometryOSM.length > 0 && dataJson.geometryOSM.filter(item => {
            // Handle both object format {id, geometry} and direct coordinate array format
            const coords = item?.geometry || item;
            return Array.isArray(coords) && coords.length > 0;
          }).map((item, index) => {
            const coords = item?.geometry || item;
            const osmId = item?.id || index;
            const roadId = `osm-${osmId}`;
            const isSelectedRoad = selectedRoadId === roadId;

            return (
              <Polyline
                key={roadId}
                pathOptions={{
                  ...optionsOSM,
                  weight: isSelectedRoad ? 6 : 3,
                  opacity: isSelectedRoad ? 1 : (hasMapSelection ? 0.45 : 0.6),
                  color: selectionAwareColor(isSelectedRoad, hasMapSelection, optionsOSM.color)
                }}
                positions={coords}
                eventHandlers={{
                  click: () => {
                    if (onSelectRoad) {
                      onSelectRoad(roadId, {
                        osmId: osmId,
                        osmName: (item?.display_name && item.display_name !== null) ? item.display_name : (item?.name || null),
                        coordinates: coords
                      });
                    }
                  }
                }}
              />
            );
          })}

          {/* Glowing polyline effect for selected OSM road - follows street shape */}
          {selectedRoadId && dataJson.geometryOSM && dataJson.geometryOSM.map((item, index) => {
            const coords = item?.geometry || item;
            const osmId = item?.id || index;
            const roadId = `osm-${osmId}`;
            if (roadId !== selectedRoadId) return null;

            if (!Array.isArray(coords) || coords.length === 0) return null;

            return (
              <React.Fragment key={`glow-road-${osmId}`}>
                {/* Outer glow layer 1 - largest, most transparent */}
                <Polyline
                  positions={coords}
                  pathOptions={{
                    color: '#8C74FF',
                    weight: 20,
                    opacity: 0.3,
                    className: 'road-glow-outer'
                  }}
                  className="road-glow-outer"
                />
                {/* Middle glow layer 2 */}
                <Polyline
                  positions={coords}
                  pathOptions={{
                    color: '#8C74FF',
                    weight: 14,
                    opacity: 0.5,
                    className: 'road-glow-middle'
                  }}
                  className="road-glow-middle"
                />
                {/* Inner glow layer 3 - brightest */}
                <Polyline
                  positions={coords}
                  pathOptions={{
                    color: '#8C74FF',
                    weight: 8,
                    opacity: 0.8,
                    className: 'road-glow-inner'
                  }}
                  className="road-glow-inner"
                />
              </React.Fragment>
            );
          })}

          {/* Glowing polyline effect for selected coda media road - follows street shape */}
          {selectedRoadId && selectedId && dataJson.nodiAimsun && dataJson.nodiAimsun.map(node => {
            if (node.id !== selectedId) return null;
            
            return node.codePerNodo[1]?.codeMedie?.geometryCodaMedia?.map((coordinates, index) => {
              const roadId = `${node.id}-road-${index}`;
              if (roadId !== selectedRoadId) return null;
              
              if (!coordinates || coordinates.length === 0) return null;

              return (
                <React.Fragment key={`glow-road-${node.id}-${index}`}>
                  {/* Outer glow layer 1 - largest, most transparent */}
                  <Polyline
                    positions={coordinates}
                    pathOptions={{
                      color: '#8C74FF',
                      weight: 20,
                      opacity: 0.3,
                      className: 'road-glow-outer'
                    }}
                    className="road-glow-outer"
                  />
                  {/* Middle glow layer 2 */}
                  <Polyline
                    positions={coordinates}
                    pathOptions={{
                      color: '#8C74FF',
                      weight: 14,
                      opacity: 0.5,
                      className: 'road-glow-middle'
                    }}
                    className="road-glow-middle"
                  />
                  {/* Inner glow layer 3 - brightest */}
                  <Polyline
                    positions={coordinates}
                    pathOptions={{
                      color: '#8C74FF',
                      weight: 8,
                      opacity: 0.8,
                      className: 'road-glow-inner'
                    }}
                    className="road-glow-inner"
                  />
                </React.Fragment>
              );
            });
          })}

          {dataJson.nodiAimsun && dataJson.nodiAimsun.map(node => {
            if (node.id === selectedId) {
              const geometryCodaMax = node.codePerNodo[0]?.codeMax?.geometryCodaMax;
              if (geometryCodaMax) {
                const parsedCoordinates = geometryCodaMax.map(coordinates => coordinates.map(coordinate => [coordinate[0], coordinate[1]]));
                return <Polyline key={`coda-max-${node.id}`} pathOptions={optionCodaMax} positions={parsedCoordinates} />;
              }
            }
            return null;
          })}

          {/* Display coda media roads - only show when a node is selected */}
          {selectedId && dataJson.nodiAimsun && dataJson.nodiAimsun.map(node => {
            // Only show coda media for the selected node
            if (node.id !== selectedId) {
              return null;
            }

            return node.codePerNodo[1]?.codeMedie && node.codePerNodo[1]?.codeMedie.geometryCodaMedia &&
              node.codePerNodo[1]?.codeMedie.geometryCodaMedia.map((coordinates, index) => {
                const roadId = `${node.id}-road-${index}`;
                const isSelectedRoad = selectedRoadId === roadId;

                return (
                  <Polyline
                    key={`coda-media-${node.id}-${index}`}
                    pathOptions={{
                      ...optionsCodaMedia,
                      weight: isSelectedRoad ? 6 : 3,
                      opacity: isSelectedRoad ? 1 : 0.8,
                      color: selectionAwareColor(isSelectedRoad, Boolean(selectedRoadId), optionsCodaMedia.color)
                    }}
                    positions={coordinates}
                    eventHandlers={{
                      click: () => {
                        if (onSelectRoad) {
                          onSelectRoad(roadId, {
                            nodeId: node.id,
                            roadIndex: index,
                            coordinates: coordinates
                          });
                        }
                      }
                    }}
                  />
                );
              });
          })}
        </MapContainer>
      </div>
      <div className="los-container-confronto" >
        {selectedId && <LosGraph nodeId={selectedId} selectedScenarioLos={selectedIdScenario} data={dataJson} style={{ height: "100%", width: "100%" }} />}
      </div>
    </div>
  );
};

export default SimpleMap;
